import scriptjs from 'scriptjs';
import deepmerge from 'deepmerge';
import config from 'config';
import constants from '../qti/Constants';
import responseService from './ResponseService';
import storeManager from '../store/StoreManager';
import responseReader from '../store/readHelpers/ResponseReader';
import studentHighlightsActions from '../actions/StudentHighlightsActions';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import { t } from './i18nService';
import sessionActions from '../actions/SessionActions';
import sessionReader from '../store/readHelpers/SessionReader';
import scorableItemReader from '../store/readHelpers/ScorableItemReader';
import objectTypes from '../constants/ObjectTypes';
import alertService from './AlertService';
import a11yService from './a11yService';

const events = {
    HYPOTHESIS_PATH_CHANGE: 'Hypothesis:pathChange',
    HYPOTHESIS_ANNOTATION_REMOVED: 'Hypothesis:annotationRemoved',
    HYPOTHESIS_DESTROY: 'Hypothesis:destroy',
    HYPOTHESIS_INIT: 'Hypothesis:init',
    HYPOTHESIS_FOCUS_ANNOTATION: 'Hypothesis:focusAnnotation',
    HYPOTHESIS_SET_VISIBILITY: 'Hypothesis:setVisibility',
};

const addAnnotation = (annotation, isHighlight) => {
    const state = storeManager.getState();
    const store = storeManager.getStore();
    const { studentHighlights: { annotatingContentId, annotatingContentType } } = state;

    const questionAnnotations = deepmerge([], getAnnotationsFromResponse(annotatingContentId));
    questionAnnotations.push({
        selector: annotation,
        type: annotatingContentType,
        isHighlight,
    });

    responseService.storeQuestionAnnotations(annotatingContentId, questionAnnotations);
    storeTestAnnotations();

    annotationService.setVisibility(true);
    if (!isHighlight) {
        // open sidebar and focus the annotation if `note` was clicked
        annotationService.setFocusedAnnotation(annotation);
        annotationService.dispatchFocusAnnotationEvent(annotation);
    }

    if (!sessionReader.isAnnotationsSidebarOpen() && !isHighlight) {
        store.dispatch(sessionActions.setAnnotationsSidebarOpen(true));
    }
};

const annotationRemoverFilter = (selector, annotation) => !equalSelectors(selector, annotation.selector);

const equalSelectors = (leftSelectors, rightSelectors) => (
    leftSelectors.every((leftSelector) => {
        const rightSelector = getSelectorByType(rightSelectors, leftSelector.type);
        if (!rightSelector) {
            return false;
        }

        return Object.keys(leftSelector).every(selectorKey => (
            leftSelector[selectorKey] === rightSelector[selectorKey]
        ));
    })
);

const getSelectorByType = (selectors, type) => selectors.find(selector => selector.type === type);

const dispatchAnnotationRemovedEvent = () => {
    window.dispatchEvent(new Event(events.HYPOTHESIS_ANNOTATION_REMOVED));
};

const getHypothesisAnnotations = () => {
    // when provide data to the hypothesis library, need to cut off our additional data and left only selectors
    return getSectionAnnotations().map(annotation => ({
        selector: annotation.selector,
        isHighlight: !!annotation.isHighlight,
    }));
};

const removeHighlight = (selector) => {
    const state = storeManager.getState();
    const { studentHighlights: { annotatingContentId } } = state;
    annotationService.removeAnnotation(annotatingContentId, selector, true);
};

const getSectionAnnotations = () => {
    const state = storeManager.getState();
    const { currentlyShown: { sectionId, questionId } } = state;
    const processedIds = [];

    let sectionAnnotations;
    if (assessmentReader.useVerticalDisplayForSection(sectionId)) {
        // get annotations for whole section
        sectionAnnotations = [];
        assessmentReader.getSectionQuestionIds(sectionId).forEach((qid) => {
            processedIds.push(qid);
            sectionAnnotations = [...sectionAnnotations, ...(getAnnotationsFromResponse(qid))];
        });
    } else {
        // get annotations for current shown question and its own stimulus if needed
        processedIds.push(questionId);
        const questionAnnotations = getAnnotationsFromResponse(questionId);

        const questionStimulus = assessmentReader.getQuestion(questionId)?.stimulus;
        const questionStimulusAnnotations = questionStimulus ? getAnnotationsFromResponse(questionStimulus.contentRevisionId) : [];
        sectionAnnotations = [...questionAnnotations, ...questionStimulusAnnotations];
    }

    // add stimulus contentRevisionId if needed
    const stimulus = assessmentReader.getStimulusForSectionId(sectionId);
    if (stimulus && stimulus.contentRevisionId && !processedIds.includes(stimulus.contentRevisionId)) {
        return [...sectionAnnotations, ...getAnnotationsFromResponse(stimulus.contentRevisionId)];
    }

    return sectionAnnotations;
};

const getAnnotationsFromResponse = (questionId) => {
    const itemResult = responseReader.getItemResultForQuestion(questionId);
    return (itemResult && itemResult.questionAnnotations) || [];
};

const composeExtendedAnnotations = (viewItem) => {
    const contentRevisionId = viewItem.contentRevisionId;
    const questionAnnotations = getAnnotationsFromResponse(contentRevisionId);
    if (questionAnnotations.length === 0) {
        return [];
    }

    const contentId = viewItem.objectType === objectTypes.STIMULUS ? viewItem.questionId : contentRevisionId;
    let questionNumber = scorableItemReader.useNumberPerInteraction()
        ? scorableItemNumberingService.getQuestionNumber(contentId)
        : assessmentReader.getQuestion(contentId)?.number;

    if (questionNumber === 'undefined') {
        questionNumber = null;
    }

    const scorableItems = scorableItemReader.getScorableItemsInQuestion(contentRevisionId);
    const stimulusNumber = scorableItemNumberingService.getStimulusFormatNumber(viewItem.sectionId);

    return questionAnnotations
        .filter(annotations => !!annotations)
        .map((annotation) => {
            const textPositionSelector = annotation.selector.find(item => item.type === 'TextPositionSelector');
            const extendedAnnotation = {
                id: `${contentId}-${textPositionSelector.start}-${textPositionSelector.end}`,
                selector: [...annotation.selector],
                isHighlight: annotation.isHighlight,
                data: {
                    contentId,
                    number: annotation.type === objectTypes.CATEGORY.STIMULUS ? stimulusNumber : questionNumber,
                    sectionId: viewItem.sectionId,
                    scorableItem: scorableItems[0]?.orderNumber,
                    type: annotation.type,
                },
            };
            if (!annotation.isHighlight && annotation.note) {
                extendedAnnotation.note = annotation.note;
            }

            return extendedAnnotation;
        })
        .sort((a, b) => a.selector[1].start - b.selector[1].start);
};

const getTestAnnotations = () => {
    // get annotations for whole test and add questionData there
    let testAnnotations = [];
    const state = storeManager.getState();
    const { itemsOrderAllView = [] } = state.toc;
    const processedIds = [];

    itemsOrderAllView.forEach((viewItem) => {
        if (!processedIds.includes(viewItem.contentRevisionId)) {
            testAnnotations = [...testAnnotations, ...(composeExtendedAnnotations(viewItem))];
            processedIds.push(viewItem.contentRevisionId);

            if (viewItem.type === objectTypes.CATEGORY.QUESTION) {
                const questionStimulus = assessmentReader.getQuestion(viewItem.contentRevisionId)?.stimulus;
                if (questionStimulus) {
                    questionStimulus.questionId = viewItem.contentRevisionId;
                    questionStimulus.sectionId = viewItem.sectionId;

                    testAnnotations = [...testAnnotations, ...(composeExtendedAnnotations(questionStimulus))];
                    processedIds.push(questionStimulus);
                }
            }
        }
    });

    return testAnnotations;
};

const storeTestAnnotations = () => {
    const store = storeManager.getStore();
    store.dispatch(studentHighlightsActions.setAnnotationList(getTestAnnotations()));
};

const annotationService = {
    init() {
        // every time the service inits the test part might be changed
        // so need to recollect and restore annotations
        storeTestAnnotations();

        const captions = {
            annotate: t('annotations.button.annotate'),
            annotateLabel: t('annotations.button.annotateLabel'),
            annotationEndAria: t('annotations.aria.annotationEnd'),
            annotationStartAria: t('annotations.aria.annotationStart'),
            delete: t('annotations.button.delete'),
            deleteLabel: t('annotations.button.deleteLabel'),
            highlight: t('annotations.button.highlight'),
            highlightLabel: t('annotations.button.highlightLabel'),
        };

        if (window.hypothesisConfig) {
            const state = storeManager.getState();
            const { studentHighlights: { focusedAnnotation, annotationsVisibility } } = state;

            window.dispatchEvent(
                new CustomEvent(events.HYPOTHESIS_INIT, { detail: { captions, focusedAnnotation } })
            );
            window.dispatchEvent(
                new CustomEvent(events.HYPOTHESIS_SET_VISIBILITY, { detail: { visibility: annotationsVisibility } })
            );
            return;
        }

        const state = storeManager.getState();
        const store = storeManager.getStore();

        store.dispatch(
            studentHighlightsActions.setAnnotatingContent(state.currentlyShown.questionId, objectTypes.CATEGORY.QUESTION)
        );

        window.hypothesisConfig = () => {
            return {
                disableSidebar: true,
                adderRange: {
                    root: '#appContentContainer',
                    exclude: [
                        '.ckeditor-container-wrapper',
                        '.draggable',
                        '.has-section-level-dynamic-rubric .question-rubric',
                        '.mathquill-outer-wrapper',
                        '#annotations-sidebar',
                        '#questionRubric',
                        '#rubric__top-level',
                        '#sectionRubric',
                    ],
                },
                captions,
                onAnnotationAdded: addAnnotation,
                refreshAnnotations: getHypothesisAnnotations,
                onAnnotationClick: this.setFocusedAnnotation,
                onHighlightRemove: removeHighlight,
            };
        };
        const libPath = config.hypothesisLibPath?.length ? config.hypothesisLibPath : constants.staticLibPaths.HYPOTHESIS;
        scriptjs(libPath);

        document.addEventListener('Hypothesis:adderVisibilityChanged', (e) => {
            if (e.detail.isVisible) {
                a11yService.addFocusTrap(e.detail.container);
                return;
            }

            a11yService.removeFocusTrap(e.detail.container);
        }, false);
    },

    destroy() {
        if (window.hypothesisConfig) {
            window.dispatchEvent(new Event(events.HYPOTHESIS_DESTROY));
        }
    },

    dispatchNavigationEvent() {
        const { studentHighlights: { focusedAnnotation } } = storeManager.getState();
        window.dispatchEvent(
            new CustomEvent(events.HYPOTHESIS_PATH_CHANGE, { detail: { focusedAnnotation } })
        );
    },

    removeAnnotation(questionId, selector, isHighlight = false) {
        const questionAnnotations = deepmerge([], getAnnotationsFromResponse(questionId))
            .filter(annotationRemoverFilter.bind(this, selector));
        responseService.storeQuestionAnnotations(questionId, questionAnnotations);
        storeTestAnnotations();
        dispatchAnnotationRemovedEvent();
        alertService.removedAnnotationItem(isHighlight);
    },

    getQuote({ selector }) {
        return selector.find(selectorItem => (selectorItem.type === 'TextQuoteSelector'))?.exact || null;
    },

    setNote(questionId, selector, note) {
        const questionAnnotations = deepmerge([], getAnnotationsFromResponse(questionId));
        const foundAnnotation = questionAnnotations.find(annotation => (equalSelectors(selector, annotation.selector)));

        if (foundAnnotation && foundAnnotation.note !== note) {
            foundAnnotation.note = note;
            responseService.storeQuestionAnnotations(questionId, questionAnnotations);
            storeTestAnnotations();
        }
    },

    isFocused(annotation) {
        const state = storeManager.getState();
        const { studentHighlights: { focusedAnnotation } } = state;

        if (!focusedAnnotation) {
            return false;
        }

        return equalSelectors(annotation, focusedAnnotation);
    },

    setFocusedAnnotation(annotation, isHighlight) {
        const store = storeManager.getStore();
        store.dispatch(studentHighlightsActions.setFocusedAnnotation(annotation));
        if (!isHighlight && !sessionReader.isAnnotationsSidebarOpen()) {
            store.dispatch(sessionActions.setAnnotationsSidebarOpen(true));
            if (sessionReader.isChatOpen()) {
                store.dispatch(sessionActions.setChatSidebarOpen(false));
            }
        }
    },

    dispatchFocusAnnotationEvent(selector) {
        setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent(events.HYPOTHESIS_FOCUS_ANNOTATION, { detail: { selector } })
            );
        }, 10);
    },

    setVisibility(visibility) {
        const store = storeManager.getStore();
        store.dispatch(studentHighlightsActions.setAnnotationsVisibility(visibility));
        window.dispatchEvent(
            new CustomEvent(events.HYPOTHESIS_SET_VISIBILITY, { detail: { visibility } })
        );
    },

    setAnnotatingContent(questionId, questionType) {
        const store = storeManager.getStore();
        const state = storeManager.getState();
        const { studentHighlights: { annotatingQuestionId, annotatingQuestionType } } = state;

        if (annotatingQuestionId !== questionId || annotatingQuestionType !== questionType) {
            store.dispatch(
                studentHighlightsActions.setAnnotatingContent(questionId, questionType)
            );
        }
    },
};

export default annotationService;
